/**
 * First-boot loading screen. Edd asked (fb-mrryc0q2) for "a loading screen on
 * first boot until charts and position etc has loaded", so cold-starting the app
 * shows a calm, Ardent-branded overlay instead of a bare or half-drawn chart
 * while the base map and the first GNSS fix come up.
 *
 * It dismisses as soon as BOTH are ready — the map's initial load has fired
 * (boot store) AND we have a position fix (chart-plotter store) — or after a
 * hard timeout, whichever comes first, so it can never deadlock waiting on a fix
 * that isn't coming (e.g. indoors, no GNSS). A short minimum on-screen time
 * stops it flashing when everything is already warm.
 *
 * Brand: Ardent navy (#163a70) with the nav "fix" mark — a ring with a centre
 * dot — matching the app icon.
 */
import { useEffect, useState } from 'react';
import { useBootStore } from '@/store/boot';
import { useChartPlotterStore } from '@/store';

/** Hard backstop: dismiss after this even if a fix never arrives. */
const MAX_MS = 9000;
/** Don't vanish faster than this, so a warm start doesn't flash. */
const MIN_MS = 600;
/** Small settle after ready so the first chart frame is painted underneath. */
const SETTLE_MS = 350;

export function FirstBootOverlay() {
  const mapReady = useBootStore((s) => s.mapReady);
  const hasFix = useChartPlotterStore((s) => s.ownship.position != null);
  const [visible, setVisible] = useState(true);
  const [leaving, setLeaving] = useState(false);

  // Hard timeout — the overlay can never outlive this, whatever the boot does.
  useEffect(() => {
    const t = setTimeout(() => setLeaving(true), MAX_MS);
    return () => clearTimeout(t);
  }, []);

  // Dismiss once the CHART is up, after a short settle so it is painted behind
  // the fade. Deliberately not waiting on a position any more: the chart is
  // usable without one, and gating on a fix meant staring at this screen for the
  // full nine seconds every time the receiver was cold or we were below decks,
  // long after the thing underneath was ready. Where the boat is, and how much we
  // trust it, is the fix-age chip's job and the boat's own colour, both of which
  // are on screen behind this.
  useEffect(() => {
    if (!mapReady) return;
    const t = setTimeout(() => setLeaving(true), SETTLE_MS);
    return () => clearTimeout(t);
  }, [mapReady]);

  // Run the fade-out, honouring the minimum on-screen time.
  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => setVisible(false), 260);
    return () => clearTimeout(t);
  }, [leaving]);

  const [minElapsed, setMinElapsed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), MIN_MS);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const status = !mapReady
    ? 'Loading charts…'
    : !hasFix
      ? 'Finding your position…'
      : 'Ready';

  // Hold the fade until the minimum time has passed, so it never flashes.
  const isLeaving = leaving && minElapsed;

  return (
    <div className={`acp-boot${isLeaving ? ' leaving' : ''}`} role="status" aria-live="polite">
      <div className="acp-boot-mark" aria-hidden>
        <svg viewBox="0 0 120 120" width="112" height="112">
          <circle className="acp-boot-ring" cx="60" cy="60" r="46" />
          <circle className="acp-boot-dot" cx="60" cy="60" r="9" />
        </svg>
      </div>
      <div className="acp-boot-word">ARDENT</div>
      <div className="acp-boot-status">{status}</div>
    </div>
  );
}
